import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { Spinner } from '@/components/ui/Spinner'
import { useAuth } from '@/features/auth/store'
import { ShipmentCard } from '@/features/orders/ShipmentCard'
import {
  SHOPPER_CANCELLABLE,
  cancelMyOrder,
  confirmStorefrontPayment,
  getMyOrder,
  startStorefrontCheckout,
} from '@/features/marketplace/api'
import { useStorefrontSettings } from '@/features/marketplace/useStorefront'
import { storefrontAuthMessage } from '@/routes/storefront/authMessage'
import { OrderStatusBadge, PaymentStatusBadge } from '@/routes/storefront/StorefrontStatus'
import { toast } from '@/lib/toast'

/**
 * How long to keep asking whether the payment has landed, and how often (M24-05).
 *
 * Stripe sends the shopper back here the instant the card is accepted, but what actually
 * marks the order paid is the webhook, which arrives on its own schedule -- usually inside
 * a second, occasionally later. Without this the shopper lands on their own order and reads
 * "Unpaid" immediately after paying, which is the single most alarming thing this screen
 * could tell them.
 *
 * The redirect also carries the session id, and the effect below hands it straight back to
 * the API, which asks Stripe what became of it rather than waiting to be told. That covers
 * the case the poll cannot: a deployment Stripe has no route to -- a laptop, a private
 * network -- where the webhook is never going to arrive at all. The poll stays as the
 * backstop for everything else, and both paths settle the same order exactly once.
 *
 * So: poll, briefly, and only after a redirect that claims success. Not a websocket, not a
 * spinner that blocks the page -- the order and its lines are shown throughout, and only
 * the payment line says it is still confirming.
 */
const CONFIRM_POLL_MS = 2_000
const CONFIRM_POLL_WINDOW_MS = 30_000

export function StorefrontOrderDetail() {
  const { orderId } = useParams<{ orderId: string }>()
  const status = useAuth((s) => s.status)
  const location = useLocation()
  const [search, setSearch] = useSearchParams()
  const { settings, money } = useStorefrontSettings()
  const queryClient = useQueryClient()
  const [paying, setPaying] = useState(false)
  const [payError, setPayError] = useState<string | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const [reason, setReason] = useState('')
  // Fixed at mount, so the window is thirty seconds from arriving on the page rather than
  // thirty seconds from whenever the last render happened to be.
  const [pollUntil] = useState(() => Date.now() + CONFIRM_POLL_WINDOW_MS)

  const justPaid = search.get('payment') === 'success'
  const cancelled = search.get('payment') === 'cancelled'
  // Stripe substitutes this into the success URL on the way back. Absent on a cancelled
  // return, and absent entirely on a provider that does not raise a hosted page.
  const sessionId = search.get('session_id')

  const {
    data: order,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['storefront-order', orderId],
    queryFn: () => getMyOrder(orderId!),
    enabled: status === 'authenticated' && !!orderId,
    retry: false,
    refetchInterval: (query) => {
      const current = query.state.data
      if (!justPaid || !current || current.payment_status === 'paid') return false
      return Date.now() < pollUntil ? CONFIRM_POLL_MS : false
    },
  })

  // Once per arrival, not once per render: React 18 mounts an effect twice in development
  // and the poll re-renders this component every two seconds, either of which would
  // otherwise send the same confirmation over and over.
  const confirming = useRef(false)

  useEffect(() => {
    if (status !== 'authenticated' || !orderId || !justPaid || !sessionId) return
    if (confirming.current) return
    confirming.current = true

    confirmStorefrontPayment(orderId, sessionId)
      .then((paid) => queryClient.setQueryData(['storefront-order', orderId], paid))
      .catch(() => {
        // Deliberately silent. The webhook is the path of record and the poll above is
        // still running -- telling the shopper that a confirmation they never asked for
        // failed would be alarming about something that is very likely about to resolve.
      })
      .finally(() => {
        // Only after the answer is in: a reload while the request is still out should be
        // able to ask again, and a session id left in the URL forever is one a bookmark
        // would keep re-confirming.
        const next = new URLSearchParams(window.location.search)
        next.delete('session_id')
        setSearch(next, { replace: true })
      })
  }, [status, orderId, justPaid, sessionId, queryClient, setSearch])

  const cancel = useMutation({
    mutationFn: () => cancelMyOrder(orderId!, reason.trim() || undefined),
    onSuccess: (updated) => {
      queryClient.setQueryData(['storefront-order', orderId], updated)
      void queryClient.invalidateQueries({ queryKey: ['storefront-orders'] })
      setCancelling(false)
      toast.success(
        updated.payment_status === 'refunded'
          ? 'Order cancelled. Your refund is on its way.'
          : 'Order cancelled.',
      )
    },
    onError: (err: unknown) => {
      // A refusal here is a real answer -- the shop has started on the order, or the card
      // processor declined the refund -- so the server's own words go on screen.
      setCancelling(false)
      void queryClient.invalidateQueries({ queryKey: ['storefront-order', orderId] })
      toast.error(storefrontAuthMessage(err))
    },
  })

  if (status === 'loading' || (isLoading && status === 'authenticated')) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="h-6 w-6 text-slate-400" />
      </div>
    )
  }

  if (status !== 'authenticated') {
    return (
      <Navigate
        to="/store/login"
        state={{ from: `${location.pathname}${location.search}` }}
        replace
      />
    )
  }

  if (error || !order) {
    return (
      <div className="space-y-4">
        <Alert tone="error" title="Order not found">
          {error instanceof ApiError && error.status !== 404
            ? error.message
            : 'We could not find that order on your account.'}
        </Alert>
        <Link to="/store/orders" className="inline-block">
          <Button variant="secondary">Back to my orders</Button>
        </Link>
      </div>
    )
  }

  const unpaid = order.payment_status === 'unpaid' && order.status !== 'cancelled'
  const stillConfirming = justPaid && unpaid && Date.now() < pollUntil
  // Not while a payment is still landing: cancelling an unpaid draft whose money arrives a
  // second later would leave a charge on a cancelled order.
  const canCancel = SHOPPER_CANCELLABLE.includes(order.status) && !stillConfirming

  const pay = async () => {
    setPayError(null)
    setPaying(true)
    try {
      const session = await startStorefrontCheckout(order.id)
      window.location.replace(session.url)
    } catch (err) {
      setPaying(false)
      setPayError(storefrontAuthMessage(err))
      toast.error('The card page could not be opened.')
    }
  }

  return (
    <div className="space-y-4">
      <Link
        to="/store/orders"
        className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
      >
        ← My orders
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            {order.order_number}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Placed {new Date(order.order_date).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <OrderStatusBadge status={order.status} />
          <PaymentStatusBadge status={order.payment_status} />
        </div>
      </div>

      {order.payment_status === 'paid' && justPaid && (
        <Alert tone="success" title="Payment received">
          Thank you — we are getting your order ready.
        </Alert>
      )}

      {stillConfirming && (
        <Alert tone="info" title="Confirming your payment">
          Your bank has accepted the payment and we are waiting for confirmation. This page updates
          itself.
        </Alert>
      )}

      {justPaid && unpaid && !stillConfirming && (
        <Alert tone="info" title="Still confirming">
          Confirmation is taking longer than usual. Nothing is wrong with your order — refresh in a
          minute, or check back shortly. Do not pay again.
        </Alert>
      )}

      {cancelled && (
        <Alert
          tone="info"
          title="Payment not completed"
          onDismiss={() => {
            // Clearing the flag rather than leaving it in the URL: a bookmark or a reload
            // should not keep announcing a cancellation from last week.
            search.delete('payment')
            setSearch(search, { replace: true })
          }}
        >
          Your order is saved and nothing has been charged. You can pay for it below whenever you
          are ready.
        </Alert>
      )}

      {payError && <Alert tone="error">{payError}</Alert>}

      {order.payment_status === 'refunded' && (
        <Alert tone="info" title="Refunded">
          {money(order.amount_due)} is on its way back to the card you paid with. Banks usually take
          5–10 working days to show it.
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader title="Items" />
            <CardBody className="space-y-3">
              {order.items.map((item) => (
                <div
                  key={`${item.product_id}-${item.name}`}
                  className="flex items-baseline justify-between gap-3 text-sm"
                >
                  <span className="min-w-0 text-slate-600 dark:text-slate-400">
                    {item.quantity} × {item.name}
                    <span className="text-slate-400"> @ {money(item.unit_price)}</span>
                  </span>
                  <span className="shrink-0 font-medium text-slate-900 dark:text-slate-100">
                    {money(item.line_total)}
                  </span>
                </div>
              ))}
            </CardBody>
          </Card>

          {order.shipping_address && (
            <Card>
              <CardHeader title="Delivery address" />
              <CardBody className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                {[
                  order.shipping_address.name,
                  order.shipping_address.line1,
                  order.shipping_address.line2,
                  order.shipping_address.city,
                  order.shipping_address.state,
                  order.shipping_address.postal_code,
                  order.shipping_address.country,
                ]
                  .filter(Boolean)
                  .map((line, index) => (
                    <p key={index}>{line}</p>
                  ))}
                {order.shipping_address.phone && (
                  <p className="mt-1 text-slate-400">
                    {order.shipping_address.dial_code ?? ''} {order.shipping_address.phone}
                  </p>
                )}
              </CardBody>
            </Card>
          )}

          {/* The tracking number only: a failed booking's reason is for staff, not the shopper. */}
          {order.awb_code && <ShipmentCard shipment={order} />}
        </div>

        <div className="space-y-3">
          <Card>
            <CardHeader title="Total" />
            <CardBody className="space-y-2 text-sm">
              <Row label="Goods" value={money(order.subtotal)} />
              {Number(order.discount_amount) > 0 && (
                <Row label="Discount" value={`− ${money(order.discount_amount)}`} />
              )}
              <Row label="Tax" value={money(order.tax_amount)} />
              <Row
                label="Delivery"
                value={Number(order.shipping_amount) > 0 ? money(order.shipping_amount) : 'Free'}
              />
              <div className="flex items-baseline justify-between border-t border-slate-200 pt-2 dark:border-slate-800">
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {order.payment_status === 'paid'
                    ? 'Paid'
                    : order.payment_status === 'refunded'
                      ? 'Refunded'
                      : 'To pay'}
                </span>
                <span className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {money(order.amount_due)}
                </span>
              </div>
              {order.paid_at && (
                <p className="text-xs text-slate-400">
                  Paid on {new Date(order.paid_at).toLocaleString()}
                </p>
              )}
            </CardBody>
          </Card>

          {unpaid && !stillConfirming && settings?.payments_enabled && (
            <Button className="w-full" loading={paying} onClick={pay}>
              {paying ? 'Opening the card page…' : `Pay ${money(order.amount_due)}`}
            </Button>
          )}

          {unpaid && !settings?.payments_enabled && (
            <Alert tone="info">
              Card payments are not switched on for this shop. We will contact you to arrange
              payment.
            </Alert>
          )}

          {canCancel && (
            <Button variant="secondary" className="w-full" onClick={() => setCancelling(true)}>
              Cancel order
            </Button>
          )}

          <Link
            to="/store"
            className="block text-center text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
          >
            Continue shopping
          </Link>
        </div>
      </div>

      <Modal
        open={cancelling}
        onClose={() => setCancelling(false)}
        title="Cancel this order?"
        description={
          order.payment_status === 'paid'
            ? `We will refund ${money(order.amount_due)} to the card you paid with. This cannot be undone.`
            : 'Nothing has been charged. This cannot be undone.'
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelling(false)}>
              Keep my order
            </Button>
            <Button variant="danger" loading={cancel.isPending} onClick={() => cancel.mutate()}>
              {order.payment_status === 'paid' ? 'Cancel and refund' : 'Cancel order'}
            </Button>
          </>
        }
      >
        <label
          className="block text-sm font-medium text-slate-700 dark:text-slate-300"
          htmlFor="storefront-cancel-reason"
        >
          Reason (optional)
        </label>
        <Input
          id="storefront-cancel-reason"
          className="mt-1.5"
          value={reason}
          maxLength={500}
          placeholder="Ordered by mistake"
          onChange={(event) => setReason(event.target.value)}
        />
      </Modal>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className="text-slate-900 dark:text-slate-100">{value}</span>
    </div>
  )
}
